import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import useLang from '../hooks/useLang';

function formatMoney(v) {
  return new Intl.NumberFormat('sw-TZ').format(v || 0) + ' TZS';
}

export default function GroupDetail() {
  const { T } = useLang();
  const { id } = useParams();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/groups/${id}`),
      api.get(`/members?group_id=${id}`),
      api.get(`/expenses?group_id=${id}`),
    ]).then(([g, m, e]) => {
      setGroup(g.data);
      setMembers(m.data);
      const tot = (e.data || []).reduce((s, x) => s + parseFloat(x.amount || 0), 0);
      setTotalExpenses(tot);
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-20 text-gray-500">{T.common.loading}</div>;
  if (!group) return <div className="text-center py-20 text-red-500">{T.groupDetail.notFound}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/groups" className="text-green-700 hover:text-green-900 text-sm">← {T.nav.groups}</Link>
        <span className="text-gray-400">/</span>
        <h1 className="text-2xl font-bold text-gray-800">{group.name}</h1>
      </div>

      {/* Balance — full-width hero card */}
      {group.stats?.net_balance !== undefined && (
        <div className="rounded-2xl bg-[linear-gradient(135deg,rgba(15,118,110,0.97),rgba(15,23,42,0.93))] px-6 py-5 text-white shadow-[0_16px_40px_rgba(15,23,42,0.18)]">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-200/80">{T.groupDetail.netBalance}</p>
              <p className="mt-1 text-3xl font-bold tracking-tight">{formatMoney(group.stats.net_balance)}</p>
            </div>
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/12 text-3xl shadow-inner shadow-white/10">
              🏦
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="text-2xl">🙋</div>
          <div className="text-xl font-bold text-green-800 mt-1">{group.member_count}</div>
          <div className="text-sm text-green-700">{T.groupDetail.members}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="text-2xl">💰</div>
          <div className="text-xl font-bold text-blue-800 mt-1">{formatMoney(group.stats?.total)}</div>
          <div className="text-sm text-blue-700">{T.groupDetail.totalContributions}</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="text-2xl">📋</div>
          <div className="text-xl font-bold text-purple-800 mt-1">{group.stats?.count}</div>
          <div className="text-sm text-purple-700">{T.groupDetail.transactions}</div>
        </div>
        {group.stats?.kiingilio > 0 && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
            <div className="text-2xl">🎫</div>
            <div className="text-xl font-bold text-teal-800 mt-1">{formatMoney(group.stats.kiingilio)}</div>
            <div className="text-sm text-teal-700">{T.groupDetail.kiingilio}</div>
          </div>
        )}
        {group.stats?.group_transfers > 0 && (
          <Link to={`/groups/${id}/group-transfers`} className="block bg-red-50 border border-red-200 rounded-xl p-4 hover:bg-red-100 transition-colors">
            <div className="text-2xl">🏥</div>
            <div className="text-xl font-bold text-red-700 mt-1">{formatMoney(group.stats.group_transfers)}</div>
            <div className="text-sm text-red-600">{T.groupDetail.groupTransfers}</div>
          </Link>
        )}
        {totalExpenses > 0 && (
          <Link to={`/groups/${id}/expenses`} className="block bg-orange-50 border border-orange-200 rounded-xl p-4 hover:bg-orange-100 transition-colors">
            <div className="text-2xl">🧾</div>
            <div className="text-xl font-bold text-orange-700 mt-1">{formatMoney(totalExpenses)}</div>
            <div className="text-sm text-orange-600">{T.groupDetail.expenses}</div>
          </Link>
        )}
        {group.start_date && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="text-2xl">📅</div>
            <div className="text-sm font-bold text-gray-800 mt-1">
              {new Date(group.start_date).toLocaleDateString('sw-TZ')} → {group.end_date ? new Date(group.end_date).toLocaleDateString('sw-TZ') : '?'}
            </div>
            <div className="text-sm text-amber-700">{T.groupDetail.cycle}</div>
          </div>
        )}
        {group.monthly_contribution > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="text-2xl">💵</div>
            <div className="text-sm font-bold text-gray-800 mt-1">{new Intl.NumberFormat('sw-TZ').format(group.monthly_contribution)} TZS</div>
            <div className="text-sm text-gray-600">{T.groupDetail.monthlyFee}</div>
          </div>
        )}
      </div>

      {/* Leaders */}
      {group.leaders && group.leaders.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">{T.groupDetail.leaders}</h2>
          <div className="flex flex-wrap gap-3">
            {group.leaders.map(l => {
              const icons = { mwenyekiti: '👑', katibu: '📝', mwekahazina: '💰' };
              const labels = {
                mwenyekiti: T.members?.memberRoles?.mwenyekiti || 'Mwenyekiti',
                katibu: T.members?.memberRoles?.katibu || 'Katibu',
                mwekahazina: T.members?.memberRoles?.mwekahazina || 'Mweka Hazina',
              };
              return (
                <div key={l.id} className="flex items-center gap-2 rounded-xl bg-green-50 border border-green-100 px-3 py-2">
                  <span className="text-lg">{icons[l.role]}</span>
                  <div>
                    <p className="text-xs font-semibold text-green-800">{labels[l.role]}</p>
                    <p className="text-sm font-medium text-gray-800">{l.name}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3">
        <Link to={`/members?group_id=${id}`}
          className="bg-white border border-gray-200 hover:border-green-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          👥 {T.groupDetail.viewMembers}
        </Link>
        <Link to={`/transactions?group_id=${id}`}
          className="bg-white border border-gray-200 hover:border-green-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          💰 {T.groupDetail.viewContributions}
        </Link>
        <Link to={`/fines?group_id=${id}`}
          className="bg-white border border-gray-200 hover:border-amber-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          ⚠️ {T.groupDetail.viewFines}
        </Link>
        <Link to={`/changiwa?group_id=${id}`}
          className="bg-white border border-gray-200 hover:border-green-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          🤝 {T.groupDetail.viewChangiwa}
        </Link>
        <Link to={`/transactions/import?group_id=${id}`}
          className="bg-white border border-gray-200 hover:border-green-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          📥 {T.groupDetail.viewImport}
        </Link>
        <Link to={`/groups/${id}/monthly`}
          className="bg-white border border-gray-200 hover:border-blue-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          📅 {T.groupDetail.viewMonthly}
        </Link>
        <Link to={`/groups/${id}/distribution`}
          className="bg-white border border-gray-200 hover:border-blue-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          📊 {T.groupDetail.viewDistribution}
        </Link>
        <Link to={`/groups/${id}/expenses`}
          className="bg-white border border-gray-200 hover:border-orange-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          🧾 {T.groupDetail.viewExpenses}
        </Link>
        <Link to={`/groups/${id}/notify`}
          className="bg-white border border-gray-200 hover:border-purple-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          🔔 {T.groupDetail.viewNotifications || 'Arifa'}
        </Link>
        <Link to={`/groups/${id}/settings`}
          className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          ⚙️ {T.groupDetail.viewSettings}
        </Link>
      </div>

      {/* Members table */}
      <div>
        <h2 className="text-lg font-semibold text-gray-700 mb-3">{T.groupDetail.members} ({members.length})</h2>
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-gray-500 text-xs uppercase">
                <th className="text-left px-4 py-3">{T.common.name}</th>
                <th className="text-left px-4 py-3">{T.common.phone}</th>
                <th className="text-left px-4 py-3">{T.members.role}</th>
                <th className="text-right px-4 py-3">{T.groupDetail.totalContributed}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/members/${m.id}`} className="text-green-700 hover:underline font-medium">
                      {m.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{m.phone}</td>
                  <td className="px-4 py-3 capitalize text-gray-500">{m.role}</td>
                  <td className="px-4 py-3 text-right font-semibold text-green-700">
                    {new Intl.NumberFormat('sw-TZ').format(m.total_contributed)}
                  </td>
                </tr>
              ))}
              {!members.length && (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-gray-400">
                    {T.groupDetail.noMembers}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
